/**
 * Why a labeller could not judge a capture (POS2-1083). The ids are
 * `labelling_actions.cant_verify_reason`, whose CHECK holds these same four codes.
 *
 * A fixed list rather than free text, because the point of recording a reason is
 * aggregating it — per labeller, per site, and above all per device, where a run of
 * them means a camera fault rather than a queue problem.
 *
 * Order is the order they are offered: the three capture faults first, then the one
 * case where the photo is fine and the contents still cannot be named.
 *
 * Labels live in code, as `l1-diagnostic-reasons.ts` does — nothing but the UI reads
 * them, so a reference table would be a migration to change a word.
 */
export const CANT_VERIFY_REASONS = [
  {
    id: "no_image",
    label: "No image",
    hint: "The capture never arrived",
  },
  {
    id: "image_obstructed",
    // Deliberately the classifier's own wording for `obstructed_poor_image`, which
    // is the one code where the labeller's reason and the model's reason mean the
    // same thing and are worth reading side by side.
    label: "Obstructed or poor image",
    hint: "Something is in the way, or the frame is wrong",
  },
  {
    id: "image_wont_load",
    label: "Image will not load",
    hint: "The photo exists but never appears",
  },
  {
    id: "cant_identify_item",
    label: "Cannot identify the item",
    hint: "The photo is fine — what is in it is not nameable",
  },
] as const;

export type CantVerifyReason = (typeof CANT_VERIFY_REASONS)[number]["id"];

/** Human label for a reason id, falling back to the raw id. */
export function cantVerifyReasonLabel(id: string): string {
  return CANT_VERIFY_REASONS.find((reason) => reason.id === id)?.label ?? id;
}
